import React, { useState, useEffect } from 'react';
import { Model, ModelCategory, Manufacturer } from '../types';
import { ModelsService, ModelCategoryService, ManufacturerService } from '../services/api';
import {
  PartsContainer,
  PartsTitle,
  SectionTitle,
  PartsTable,
  TableHeader,
  TableCell,
  PartDescription,
  BooleanIndicator,
  ActionButton,
  HeaderContainer,
  LoadingContainer,
  ErrorContainer
} from './PartsManagement.styles';

const StatusBadge = ({ discontinued }: { discontinued: boolean }) => (
  <BooleanIndicator $value={!discontinued}>
    {discontinued ? 'Discontinued' : 'Active'}
  </BooleanIndicator>
);

const ModelManagement: React.FC = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [categories, setCategories] = useState<ModelCategory[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    manufacturer: '',
    category: '',
    description: '',
    releaseDate: '',
    discontinued: false
  });

  // Load models, categories and manufacturers from API
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all data in parallel
      const [fetchedModels, fetchedCategories, fetchedManufacturers] = await Promise.all([
        ModelsService.getAllModels(),
        ModelCategoryService.getAllModelCategories(),
        ManufacturerService.getAllManufacturers()
      ]);
      
      setModels(fetchedModels);
      setCategories(fetchedCategories.filter(c => c.isActive));
      setManufacturers(fetchedManufacturers.filter(m => m.isActive));
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredModels = models.filter(model =>
    model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateModel = () => {
    setEditingModel(null);
    setFormData({
      name: '',
      manufacturer: '',
      category: '',
      description: '',
      releaseDate: '',
      discontinued: false
    });
    setShowForm(true);
  };

  const handleEditModel = (model: Model) => {
    setEditingModel(model);
    setFormData({
      name: model.name,
      manufacturer: model.manufacturer,
      category: model.category,
      description: model.description || '',
      releaseDate: model.releaseYear ? `${model.releaseYear}-01-01` : '',
      discontinued: model.discontinued
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      manufacturer: '',
      category: '',
      description: '',
      releaseDate: '',
      discontinued: false
    });
    setEditingModel(null);
    setShowForm(false);
  };

  const handleSaveModel = async () => {
    try {
      setError(null);
      
      // Convert release date to year for the API
      const releaseYear = formData.releaseDate ? new Date(formData.releaseDate).getFullYear() : undefined;
      
      if (editingModel) {
        // Update existing model
        const updatedModel = await ModelsService.updateModel(editingModel.id, {
          name: formData.name,
          manufacturer: formData.manufacturer,
          category: formData.category,
          description: formData.description,
          releaseYear: releaseYear,
          discontinued: formData.discontinued
        });

        setModels(models.map(m => m.id === editingModel.id ? updatedModel : m));
      } else {
        // Create new model
        const newModel = await ModelsService.createModel({
          name: formData.name,
          manufacturer: formData.manufacturer,
          category: formData.category,
          description: formData.description,
          releaseYear: releaseYear,
          discontinued: formData.discontinued
        });

        setModels([...models, newModel]);
      }

      setShowForm(false);
    } catch (error) {
      console.error('Error saving model:', error);
      setError('Failed to save model. Please try again.');
    }
  };

  const handleDeleteModel = async (modelId: string) => {
    if (window.confirm('Are you sure you want to delete this model? This action cannot be undone.')) {
      try {
        setError(null);
        await ModelsService.deleteModel(modelId);
        setModels(models.filter(m => m.id !== modelId));
      } catch (error) {
        console.error('Error deleting model:', error);
        setError('Failed to delete model. Please try again.');
      }
    }
  };



  if (loading) {
    return (
      <PartsContainer>
        <PartsTitle>Model Management</PartsTitle>
        <LoadingContainer>Loading models...</LoadingContainer>
      </PartsContainer>
    );
  }

  return (
    <PartsContainer>
      <HeaderContainer>
        <PartsTitle>Model Management</PartsTitle>
        <ActionButton onClick={handleCreateModel}>
          Add New Model
        </ActionButton>
      </HeaderContainer>
      
      {error && (
        <ErrorContainer data-testid="error-message">
          {error}
          <ActionButton onClick={() => setError(null)}>
            Dismiss
          </ActionButton>
        </ErrorContainer>
      )}

      <div className="form-section">
        <label htmlFor="search">Search Models</label>
        <input
          type="text"
          id="search"
          className="form-control"
          placeholder="Search by name, manufacturer, or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {showForm && (
        <div className="form-section">
          <SectionTitle>
            {editingModel ? 'Edit Model' : 'Create New Model'}
          </SectionTitle>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSaveModel(); }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="name">Model Name *</label>
                <input
                  type="text"
                  id="name"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="manufacturer">Manufacturer *</label>
                <select
                  id="manufacturer"
                  className="form-control"
                  value={formData.manufacturer}
                  onChange={(e) => setFormData(prev => ({ ...prev, manufacturer: e.target.value }))}
                  required
                >
                  <option value="">Select a manufacturer...</option>
                  {manufacturers.map(manufacturer => (
                    <option key={manufacturer.id} value={manufacturer.name}>
                      {manufacturer.name}
                    </option>
                  ))}
                </select>
                {manufacturers.length === 0 && (
                  <small style={{ color: '#666', fontStyle: 'italic' }}>
                    No manufacturers available. Admin users can create manufacturers in Manufacturer Management.
                  </small>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  className="form-control"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  required
                >
                  <option value="">Select a category...</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {categories.length === 0 && (
                  <small style={{ color: '#666', fontStyle: 'italic' }}>
                    No categories available. Admin users can create categories in Model Category Management.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="releaseDate">Release Date</label>
                <input
                  type="date"
                  id="releaseDate"
                  className="form-control"
                  value={formData.releaseDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, releaseDate: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                className="form-control"
                placeholder="Optional description"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.discontinued}
                  onChange={(e) => setFormData(prev => ({ ...prev, discontinued: e.target.checked }))}
                />
                {' '}Mark as discontinued
              </label>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" className="btn btn-primary">
                {editingModel ? 'Update Model' : 'Create Model'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="form-section">
        <SectionTitle>Models List ({filteredModels.length} models)</SectionTitle>
        <PartsTable>
          <thead>
            <tr>
              <TableHeader>Name</TableHeader>
              <TableHeader>Manufacturer</TableHeader>
              <TableHeader>Category</TableHeader>
              <TableHeader>Release Year</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {filteredModels.map(model => (
              <tr key={model.id}>
                <TableCell>
                  <strong>{model.name}</strong>
                  {model.description && (
                    <PartDescription>
                      {model.description}
                    </PartDescription>
                  )}
                </TableCell>
                <TableCell>{model.manufacturer}</TableCell>
                <TableCell>{model.category}</TableCell>
                <TableCell>{model.releaseYear || 'N/A'}</TableCell>
                <TableCell>
                  <StatusBadge discontinued={model.discontinued} />
                </TableCell>
                <TableCell>
                  <ActionButton
                    onClick={() => handleEditModel(model)}
                  >
                    Edit
                  </ActionButton>
                  <ActionButton
                    $variant="danger"
                    onClick={() => handleDeleteModel(model.id)}
                  >
                    Delete
                  </ActionButton>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </PartsTable>

        {filteredModels.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            {searchTerm ? 'No models found matching your search.' : 'No models available. Create your first model.'}
          </div>
        )}
      </div>
    </PartsContainer>
  );
};

export default ModelManagement;